import React, { useState } from 'react';
import { TrendingUp, Copy, Trash2, DollarSign } from 'lucide-react';

const ProfitMarginCalculatorTool = () => {
  const [mode, setMode] = useState('margin');
  const [revenue, setRevenue] = useState('');
  const [cost, setCost] = useState('');
  const [targetMargin, setTargetMargin] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    if (mode === 'margin') {
      const rev = parseFloat(revenue);
      const c = parseFloat(cost);
      
      if (isNaN(rev) || isNaN(c)) {
        setResult(null);
        return;
      }

      const grossProfit = rev - c;
      const grossMargin = (grossProfit / rev) * 100;
      const markup = (grossProfit / c) * 100;

      setResult({
        mainValue: grossMargin,
        mainLabel: 'Gross Profit Margin',
        breakdown: [
          { label: 'Revenue', value: `$${rev.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Cost', value: `$${c.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Gross Profit', value: `$${grossProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Gross Margin', value: `${grossMargin.toFixed(2)}%` },
          { label: 'Markup', value: `${markup.toFixed(2)}%` }
        ]
      });
    } else if (mode === 'price') {
      const c = parseFloat(cost);
      const margin = parseFloat(targetMargin);

      if (isNaN(c) || isNaN(margin)) {
        setResult(null);
        return;
      }

      // Price = Cost / (1 - Margin%)
      const price = c / (1 - margin / 100);
      const profit = price - c;

      setResult({
        mainValue: price,
        mainLabel: 'Selling Price',
        isPrice: true,
        breakdown: [
          { label: 'Cost', value: `$${c.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Target Margin', value: `${margin}%` },
          { label: 'Selling Price', value: `$${price.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Profit per Unit', value: `$${profit.toLocaleString(undefined, { minimumFractionDigits: 2 })}` }
        ]
      });
    } else if (mode === 'cost') {
      const price = parseFloat(sellingPrice);
      const margin = parseFloat(targetMargin);

      if (isNaN(price) || isNaN(margin)) {
        setResult(null);
        return;
      }

      // Cost = Price × (1 - Margin%)
      const maxCost = price * (1 - margin / 100);
      const profit = price - maxCost;

      setResult({
        mainValue: maxCost,
        mainLabel: 'Maximum Cost',
        isPrice: true,
        breakdown: [
          { label: 'Selling Price', value: `$${price.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Target Margin', value: `${margin}%` },
          { label: 'Maximum Cost', value: `$${maxCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Profit per Unit', value: `$${profit.toLocaleString(undefined, { minimumFractionDigits: 2 })}` }
        ]
      });
    }
  };

  const copyResult = () => {
    if (result) {
      navigator.clipboard.writeText(result.mainValue.toFixed(2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const clearAll = () => {
    setRevenue('');
    setCost('');
    setTargetMargin('');
    setSellingPrice('');
    setResult(null);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-2">
        {[
          { key: 'margin', label: 'Calculate Margin' },
          { key: 'price', label: 'Find Selling Price' },
          { key: 'cost', label: 'Find Max Cost' }
        ].map((m) => (
          <button
            key={m.key}
            onClick={() => { setMode(m.key); setResult(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === m.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        {mode === 'margin' && (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Revenue / Selling Price ($)
              </label>
              <input
                type="number"
                value={revenue}
                onChange={(e) => setRevenue(e.target.value)}
                placeholder="e.g., 100"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Cost ($)
              </label>
              <input
                type="number"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="e.g., 60"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {mode === 'price' && (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Cost ($)
              </label>
              <input
                type="number"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="e.g., 60"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Target Margin (%)
              </label>
              <input
                type="number"
                value={targetMargin}
                onChange={(e) => setTargetMargin(e.target.value)}
                placeholder="e.g., 40"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {mode === 'cost' && (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Selling Price ($)
              </label>
              <input
                type="number"
                value={sellingPrice}
                onChange={(e) => setSellingPrice(e.target.value)}
                placeholder="e.g., 100"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Target Margin (%)
              </label>
              <input
                type="number"
                value={targetMargin}
                onChange={(e) => setTargetMargin(e.target.value)}
                placeholder="e.g., 40"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}
      </div>

      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
      >
        <TrendingUp size={18} />
        Calculate
      </button>

      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-400 mb-1">{result.mainLabel}</div>
              <div className="text-3xl font-bold text-green-400">
                {result.isPrice ? '$' : ''}{result.mainValue.toFixed(2)}{!result.isPrice ? '%' : ''}
              </div>
            </div>
            <button
              onClick={copyResult}
              className={`p-2 rounded transition-colors ${
                copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              <Copy size={18} />
            </button>
          </div>

          <div className="border-t border-gray-700 pt-4">
            {result.breakdown.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm py-1">
                <span className="text-gray-400">{item.label}</span>
                <span className="text-white font-medium">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Margin vs Markup</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">Profit Margin</div>
            <div>Profit ÷ Revenue × 100</div>
            <div className="text-xs mt-1">Percentage of revenue that is profit</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Markup</div>
            <div>Profit ÷ Cost × 100</div>
            <div className="text-xs mt-1">Percentage added to cost</div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="text-gray-500 mb-2">Example:</div>
          <div>Cost: $60, Price: $100 → Margin: 40%, Markup: 66.67%</div>
        </div>
      </div>

      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default ProfitMarginCalculatorTool;
